import { useCallback, useEffect, useState } from 'react';
import { useLocalStorage } from 'react-use';

import { currentAppEnvironment } from '@savgroup-front-common/constants';
import {
  selectSellerById,
  selectSellers,
} from '@savgroup-front-common/core/domains/sellers/selectors';
import { useSetGlobalDataSeller } from '@savgroup-front-common/core/hooks/useSetGlobalDataSeller';
import { onSessionUnauthorized } from '@savgroup-front-common/core/services/sessionCookieMode';
import { LOCAL_STORAGE_KEYS, Seller } from '@savgroup-front-common/types';

import { useControlTypedSelector } from '../../hooks';

import {
  SESSION_STATE,
  SESSION_STATE_RETRY_DELAY,
} from './App.context.constants';
import { fetchSessionState } from './App.context.helpers';
import { SessionSnapshot } from './App.context.types';

interface UseSellerSelectionResult {
  selectedSeller?: Seller;
  onSelectSeller: (sellerId?: string) => void;
}

export function useSellerSelection(): UseSellerSelectionResult {
  const [selectedSellerFromStorage, setSelectedSellerFromStorage] =
    useLocalStorage<string>(
      `${currentAppEnvironment}_${LOCAL_STORAGE_KEYS.SELECTED_SELLER}`,
      undefined,
    );

  const sellers = useControlTypedSelector(selectSellers);
  const firstSeller = sellers[0];

  const [selectedSellerId, setSelectedSellerId] = useState(
    selectedSellerFromStorage,
  );

  const onSelectSeller = useCallback(
    (sellerId?: string) => {
      setSelectedSellerId(sellerId);
      setSelectedSellerFromStorage(sellerId);
    },
    [setSelectedSellerFromStorage],
  );

  const selectedSeller = useControlTypedSelector((store) =>
    selectSellerById(store, { sellerId: selectedSellerId }),
  );

  useSetGlobalDataSeller((selectedSeller ?? firstSeller)?.id);

  return { selectedSeller: selectedSeller ?? firstSeller, onSelectSeller };
}

interface UseIframeContextResult {
  isIframeContext: boolean;
  onIframePageMount: () => void;
  onIframePageUnmount: () => void;
}

export function useIframeContext(): UseIframeContextResult {
  const [isIframeContext, setIsIframeContext] = useState(false);

  const onIframePageMount = useCallback(() => {
    setIsIframeContext(true);
  }, []);

  const onIframePageUnmount = useCallback(() => {
    setIsIframeContext(false);
  }, []);

  return { isIframeContext, onIframePageMount, onIframePageUnmount };
}

export function useSessionLossCheck(
  onSessionLost: (snapshot: SessionSnapshot) => void,
): void {
  useEffect(() => {
    let isSubscribed = true;
    let isChecking = false;
    let hasPendingCheck = false;
    let retryTimeoutId: number | undefined;

    async function checkSession(): Promise<void> {
      isChecking = true;
      hasPendingCheck = false;
      const snapshot = await fetchSessionState();

      if (!isSubscribed) {
        return;
      }

      if (snapshot.state === SESSION_STATE.UNAVAILABLE) {
        retryTimeoutId = window.setTimeout(
          checkSession,
          SESSION_STATE_RETRY_DELAY,
        );

        return;
      }

      isChecking = false;

      if (snapshot.state === SESSION_STATE.ABSENT) {
        onSessionLost(snapshot);

        return;
      }

      if (hasPendingCheck) {
        checkSession();
      }
    }

    const unsubscribe = onSessionUnauthorized(() => {
      if (isChecking) {
        hasPendingCheck = true;

        return;
      }

      checkSession();
    });

    return () => {
      isSubscribed = false;
      window.clearTimeout(retryTimeoutId);
      unsubscribe();
    };
  }, [onSessionLost]);
}
